import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationChannel, ReminderProvider, ReminderRule } from '../../interfaces/notification.interface';
import { useToast } from '../../context/ToastContext';
import { notifyNotificationsChanged, parseOffsets, sourceLabel } from './notificationText';

interface Draft {
    id?: string;
    sourceKey: string;
    offsets: string;
    channels: NotificationChannel[];
    enabled: boolean;
    userEdited: boolean;
}

const CHANNELS: NotificationChannel[] = ['IN_APP', 'EMAIL', 'SMS'];
const channelLabel: Record<NotificationChannel, string> = { IN_APP: 'In-app', EMAIL: 'E-mail digest', SMS: 'SMS (overdue)' };

const toDraft = (r: ReminderRule): Draft => ({
    id: r.id, sourceKey: r.sourceKey, offsets: r.offsetDays.join(', '), channels: r.channels,
    enabled: r.enabled, userEdited: r.userEdited,
});

const ReminderRulesPage: React.FC = () => {
    const toast = useToast();
    const [drafts, setDrafts] = useState<Draft[]>([]);
    const [providers, setProviders] = useState<ReminderProvider[]>([]);
    const [loading, setLoading] = useState(true);
    const [lastRun, setLastRun] = useState<string | null>(null);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) { setLoading(false); return; }
        try {
            const view = await NotificationService.rules(institutionId);
            setProviders(view.providers);
            const byKey = new Map(view.rules.map(r => [r.sourceKey, r]));
            // One row per registered source, whether or not a rule exists yet.
            const rows = view.providers.map(p => {
                const rule = byKey.get(p.sourceKey);
                return rule ? toDraft(rule)
                    : { sourceKey: p.sourceKey, offsets: '', channels: ['IN_APP'] as NotificationChannel[], enabled: false, userEdited: false };
            });
            setDrafts(rows);
        } catch (err) {
            toast.showApiError(err, 'Failed to load reminder rules');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const patch = (key: string, change: Partial<Draft>) =>
        setDrafts(list => list.map(d => (d.sourceKey === key ? { ...d, ...change } : d)));

    const toggleChannel = (d: Draft, c: NotificationChannel) => {
        const has = d.channels.includes(c);
        const next = has ? d.channels.filter(x => x !== c) : [...d.channels, c];
        patch(d.sourceKey, { channels: next.includes('IN_APP') ? next : ['IN_APP', ...next] });
    };

    const save = async (d: Draft) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        const offsets = parseOffsets(d.offsets);
        if (!offsets) {
            toast.showError('Enter offsets as whole days separated by commas, for example 7, 3, 0, -1');
            return;
        }
        const req = { institutionId, sourceKey: d.sourceKey, offsetDays: offsets, channels: d.channels, enabled: d.enabled };
        try {
            const saved = d.id ? await NotificationService.updateRule(d.id, req) : await NotificationService.createRule(req);
            patch(d.sourceKey, toDraft(saved));
            toast.showSuccess('Rule saved');
        } catch (err) {
            toast.showApiError(err, 'Failed to save rule');
        }
    };

    const loadDefaults = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            const r = await NotificationService.loadDefaults(institutionId);
            toast.showSuccess(`Default rules loaded: ${r.created} added, ${r.skipped} left unchanged`);
            load();
        } catch (err) {
            toast.showApiError(err, 'Failed to load default rules');
        }
    };

    const runNow = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            const r = await NotificationService.runNow(institutionId);
            notifyNotificationsChanged();
            setLastRun(`Scanned ${r.itemsScanned} item(s): ${r.notificationsCreated} new notification(s), ${r.digestsQueued} digest e-mail(s), ${r.smsSent} SMS.`);
        } catch (err) {
            toast.showApiError(err, 'Failed to run reminders');
        }
    };

    return (
        <div style={{ padding: '20px' }} data-help-page="notifications.rules.page">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h2>Reminder rules</h2>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button variant="outlined" onClick={loadDefaults} data-help="notifications.rules.loadDefaults">Load default reminder rules</Button>
                    <Button variant="contained" onClick={runNow} data-help="notifications.rules.runNow">Run now</Button>
                </Box>
            </div>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
                The reminder job runs every morning at 07:00 Bangladesh time. Offsets are days before the due date:
                7 = a week ahead, 0 = on the day, -1 = a day after. Loading defaults adds only the missing rules and never
                changes a rule you have edited.
            </Typography>
            {lastRun && <Alert severity="success" sx={{ mb: 2 }}>{lastRun}</Alert>}
            {loading ? (
                <Typography>Loading...</Typography>
            ) : providers.length === 0 ? (
                <Typography color="text.secondary">No reminder sources are registered.</Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {drafts.map(d => (
                        <Box key={d.sourceKey} data-help-section="notifications.rules.row" sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
                                <Typography variant="subtitle2">{sourceLabel(d.sourceKey)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {d.id ? (d.userEdited ? 'Edited' : 'Default') : 'No rule yet'}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: '1 1 200px', maxWidth: 260 }}>
                                <TextField
                                    label="Days before due"
                                    data-help="notifications.rules.field.offsets"
                                    size="small"
                                    fullWidth
                                    value={d.offsets}
                                    onChange={e => patch(d.sourceKey, { offsets: e.target.value })}
                                    placeholder="7, 3, 0, -1"
                                />
                            </Box>
                            <Box sx={{ flex: '2 1 280px', display: 'flex', flexWrap: 'wrap' }}>
                                {CHANNELS.map(c => (
                                    <FormControlLabel
                                        key={c}
                                        data-help="notifications.rules.field.channel"
                                        control={<Checkbox size="small" checked={d.channels.includes(c)} disabled={c === 'IN_APP'}
                                            onChange={() => toggleChannel(d, c)} />}
                                        label={channelLabel[c]}
                                    />
                                ))}
                            </Box>
                            <FormControlLabel
                                data-help="notifications.rules.field.enabled"
                                control={<Switch checked={d.enabled} onChange={e => patch(d.sourceKey, { enabled: e.target.checked })} />}
                                label="Enabled"
                            />
                            <Button variant="contained" size="small" onClick={() => save(d)} data-help="notifications.rules.save">Save</Button>
                        </Box>
                    ))}
                </Box>
            )}
        </div>
    );
};

export default ReminderRulesPage;
